import { Component, Input, OnInit, inject } from "@angular/core";
import {
  faTemperature2,
  faBell,
  faDroplet,
  faCalendar,
} from "@fortawesome/free-solid-svg-icons";
import { Store } from "@ngrx/store";
import { AppState } from "../../../../store";
import { DataService } from "../../../../services/data.service";
import { GraphqlService } from "../../../../services/graphql.service";
import { gql } from "graphql-tag";
import { responseField } from "../../../../store/base.graphql";
import { StoreItemActions } from "../../../../store/entities/store-item/store-item.actions";
import { selectStoreItems } from "../../../../store/entities/store-item/store-item.selectors";
import { StoreItemEntity } from "../../../../store/entities/store-item/store-item.model";
import { Warehouse } from "../../../../store/entities/warehouse-management/warehouse/warehouse.model";
import { GET_ALL_MSD_WAREHOUSE } from "../../../../store/entities/warehouse-management/warehouse/warehouse.graphql";
import { BaseResponse } from "../../../../common/interfaces/global-interfaces";
import jsPDF from "jspdf";
import { Observable } from "rxjs";
import { Device } from "../../../../store/entities/warehouse-management/device/device.model";
import { GET_ALL_SENSORS } from "../../../../store/entities/warehouse-management/device/device.graphql";

interface ZoneAnalysisData {
  zone_id: string;
  zone_name: string;
  avg_temperature: number;
  avg_humidity: number;
  stability_score: number;
  alerts: {
    zone_name: string;
    alert_count: number;
    alert_percentage: number;
  }[];
}

export const GET_ZONE_ANALYSIS_REPORT = gql`
    query GetWarehouseZoneAnalysisReport($filtering: BuildingReportsFilteringInputObject) {
        getWarehouseZoneAnalysisReport(filtering: $filtering) {
            response {
                ${responseField}
            }
            data {
                dashboardData
            }
        }
    }
`;

@Component({
  selector: "app-building-analysis-report",
  templateUrl: "./building-analysis-report.component.html",
  styleUrl: "./building-analysis-report.component.scss",
})
export class BuildingAnalysisReportComponent implements OnInit {
  @Input() isAllWarehouse: boolean = true;

  private store = inject(Store<AppState>);
  private ds = inject(DataService);
  private apollo = inject(GraphqlService);
  warehouseSensors$: Observable<Device[]>;
  filtering = {
    warehouse: null,
    sensor: null,
    dateFrom: this.ds.formatDate(new Date()) + "T00:00:00",
    dateTo: this.ds.formatDate(new Date()) + "T23:59:59",
  };

  warehouses$ = this.store.select(selectStoreItems(StoreItemEntity.warehouse));
  reportData: ZoneAnalysisData[] = [];
  noDataMessage: string = null;
  showChart = false;

  temperatureChartData: any[] = [];
  humidityChartData: any[] = [];
  xAxis: string[] = [];
  radialData: any[] = [];
  topStableZones: any[] = [];

  ngOnInit() {
    this.store.dispatch(
      StoreItemActions.getStoreItems({
        entity: StoreItemEntity.warehouse,
        query: GET_ALL_MSD_WAREHOUSE,
        filtering: {},
      }),
    );

    this.warehouses$.subscribe((warehouses) => {
      if (warehouses?.length) {
        this.filtering.warehouse = warehouses[0].uniqueId;

        this.generateReport();
      }
    });
    this.warehouses$.subscribe((warehouses) => {
      if (warehouses?.length) {
        this.filtering.warehouse = warehouses[0].uniqueId;

        this.store.dispatch(
          StoreItemActions.getStoreItems({
            entity: StoreItemEntity.warehouseSensor,
            query: GET_ALL_SENSORS,
            filtering: { warehouse: this.filtering.warehouse },
          }),
        );

        this.warehouseSensors$ = this.store.select(
          selectStoreItems(StoreItemEntity.warehouseSensor),
        );
        this.warehouseSensors$.subscribe((sensors) => {
          if (sensors?.length) {
            this.filtering.sensor = sensors[0].uniqueId;
            this.generateReport();
          }
        });
      }
    });
  }

  onFilterChange(
    evt: string,
    filterType: "warehouse" | "sensor" | "dateFrom" | "dateTo",
  ) {
    switch (filterType) {
      case "warehouse":
        this.filtering.warehouse = evt;

        this.store.dispatch(
          StoreItemActions.getStoreItems({
            entity: StoreItemEntity.warehouseSensor,
            query: GET_ALL_SENSORS,
            filtering: { warehouse: evt },
          }),
        );

        this.warehouseSensors$ = this.store.select(
          selectStoreItems(StoreItemEntity.warehouseSensor),
        );
        this.warehouseSensors$.subscribe((sensors) => {
          if (sensors?.length) {
            this.filtering.sensor = sensors[0].uniqueId;
            this.generateReport();
          }
        });
        break;

      case "sensor":
        this.filtering.sensor = evt;
        this.generateReport();
        break;

      case "dateFrom":
        this.filtering.dateFrom = evt;
        break;

      case "dateTo":
        this.filtering.dateTo = evt;
        break;
    }
  }
  async generateReport() {
    try {
      const response: any = await this.apollo.query({
        query: GET_ZONE_ANALYSIS_REPORT,
        variables: { filtering: this.filtering },
      });

      const result: BaseResponse<{ dashboardData: string }> = Object.values(
        Object.values(response)[0],
      )[0];

      if (result?.response?.status && result?.data?.dashboardData) {
        this.noDataMessage = null;
        this.reportData = JSON.parse(result.data.dashboardData);
        this.transformDataForCharts();
        this.showChart = true;
      } else {
        this.reportData = [];
        this.showChart = false;
        this.noDataMessage = result?.response?.message || 'No data available.';
      }
    } catch (e) {
      this.reportData = [];
      this.showChart = false;
      this.noDataMessage = e.message || 'An error occurred while loading the report.';
    }
  }

  async generatePDF() {
    const pdf = new jsPDF("p", "pt", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 30;
    let yPos = margin;

    // Add logo and header
    pdf.addImage("/assets/images/msd_logo.png", "PNG", margin, yPos, 80, 40);
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text("BUILDING ANALYSIS REPORT", pageWidth / 2, yPos + 30, {
      align: "center",
    });

    // Document info
    pdf.setFontSize(10);
    const rightX = pageWidth - margin;
    pdf.text(
      `Generated: ${new Date().toLocaleDateString()}`,
      rightX,
      yPos + 60,
      { align: "right" },
    );
    pdf.text("Validity: 3 Years", rightX, yPos + 80, { align: "right" });

    yPos += 100;

    // Warehouse info
    pdf.setFontSize(12);
    // const warehouseName = this.warehouses$.value?.find(w => w.uniqueId === this.filtering.warehouse)?.name || '';
    // pdf.text(`Warehouse: ${warehouseName}`, margin, yPos);
    pdf.text(
      `Date Range: ${this.filtering.dateFrom} to ${this.filtering.dateTo}`,
      margin,
      yPos + 20,
    );
    yPos += 50;

    // Add summary section
    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.text("Environmental Summary", margin, yPos);
    yPos += 30;

    // Summary metrics
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    pdf.text(`Total Alerts: ${this.getTotalAlerts()}`, margin, yPos);
    pdf.text(
      `Most Alerts: ${this.getZoneWithMostAlerts().count} (${this.getZoneWithMostAlerts().name})`,
      margin + 150,
      yPos,
    );
    yPos += 20;

    // Temperature Data
    pdf.setFont("helvetica", "bold");
    pdf.text("Temperature Analysis (°C)", margin, yPos);
    yPos += 15;
    this.addZoneDataTable(
      pdf,
      "Temperature",
      this.reportData.map((z) => z.avg_temperature),
      margin,
      yPos,
    );
    yPos += 100;

    // Humidity Data
    pdf.setFont("helvetica", "bold");
    pdf.text("Humidity Analysis (%)", margin, yPos);
    yPos += 15;
    this.addZoneDataTable(
      pdf,
      "Humidity",
      this.reportData.map((z) => z.avg_humidity),
      margin,
      yPos,
    );
    yPos += 100;

    // Stability Scores
    pdf.setFont("helvetica", "bold");
    pdf.text("Top Stable Zones", margin, yPos);
    yPos += 20;
    this.addStabilityTable(pdf, margin, yPos);
    yPos += 80;

    // Footer
    pdf.setFontSize(10);
    pdf.setTextColor(0);
    const bottomY = pageHeight - margin;
    pdf.text("Revision No: 01", margin, bottomY);
    pdf.text("Effective Date: 09/08/2024", margin + 150, bottomY);
    pdf.text("Review Date: 08/08/2027", margin + 350, bottomY);

    pdf.save(
      `Warehouse_Analysis_Report_${new Date().toISOString().split("T")[0]}.pdf`,
    );
  }

  private addZoneDataTable(
    pdf: jsPDF,
    title: string,
    data: number[],
    x: number,
    y: number,
  ) {
    const colWidth = 100;
    const rowHeight = 20;

    // Header
    pdf.setFillColor(45, 87, 144);
    pdf.rect(x, y, colWidth, rowHeight, "F");
    pdf.setTextColor(255);
    pdf.text("Zone", x + 5, y + 15);

    pdf.rect(x + colWidth, y, colWidth, rowHeight, "F");
    pdf.text(title, x + colWidth + 5, y + 15);

    // Rows
    this.reportData.forEach((zone, index) => {
      const yPos = y + (index + 1) * rowHeight;
      pdf.setFillColor(index % 2 === 0 ? 255 : 232, 242, 252);
      pdf.rect(x, yPos, colWidth, rowHeight, "F");
      pdf.rect(x + colWidth, yPos, colWidth, rowHeight, "F");

      pdf.setTextColor(0);
      pdf.text(zone.zone_name, x + 5, yPos + 15);
      pdf.text(data[index].toFixed(2), x + colWidth + 5, yPos + 15);
    });
  }

  private addStabilityTable(pdf: jsPDF, x: number, y: number) {
    const colWidth = [40, 200, 80];
    const rowHeight = 20;

    // Header
    pdf.setFillColor(45, 87, 144);
    pdf.rect(x, y, colWidth[0], rowHeight, "F");
    pdf.rect(x + colWidth[0], y, colWidth[1], rowHeight, "F");
    pdf.rect(x + colWidth[0] + colWidth[1], y, colWidth[2], rowHeight, "F");

    pdf.setTextColor(255);
    pdf.text("#", x + 5, y + 15);
    pdf.text("Zone Name", x + colWidth[0] + 5, y + 15);
    pdf.text("Score", x + colWidth[0] + colWidth[1] + 5, y + 15);

    // Rows
    this.topStableZones.forEach((zone, index) => {
      const yPos = y + (index + 1) * rowHeight;
      pdf.setFillColor(index % 2 === 0 ? 255 : 232, 242, 252);
      pdf.rect(x, yPos, colWidth[0], rowHeight, "F");
      pdf.rect(x + colWidth[0], yPos, colWidth[1], rowHeight, "F");
      pdf.rect(
        x + colWidth[0] + colWidth[1],
        yPos,
        colWidth[2],
        rowHeight,
        "F",
      );

      pdf.setTextColor(0);
      pdf.text(`${index + 1}`, x + 5, yPos + 15);
      pdf.text(zone.zone_name, x + colWidth[0] + 5, yPos + 15);
      pdf.text(
        `${zone.stability_score.toFixed(1)}%`,
        x + colWidth[0] + colWidth[1] + 5,
        yPos + 15,
      );
    });
  }

  private transformDataForCharts() {
    // Temperature and Humidity Bar Charts
    this.xAxis = this.reportData.map((zone) => zone.zone_name);
    this.temperatureChartData = [
      {
        name: "Temperature",
        data: this.reportData.map((zone) => zone.avg_temperature),
        color: "#FD9034",
      },
    ];
    this.humidityChartData = [
      {
        name: "Humidity",
        data: this.reportData.map((zone) => zone.avg_humidity),
        color: "#23AFF1",
      },
    ];

    // Radial Chart for Alerts
    this.radialData = this.reportData.map((zone) => ({
      name: zone.zone_name,
      data: zone.alerts.reduce((sum, alert) => sum + alert.alert_count, 0),
      color: this.colorPicker(),
    }));

    // Top Stable Zones
    this.topStableZones = [...this.reportData]
      .sort((a, b) => b.stability_score - a.stability_score)
      .slice(0, 5);
  }

  getTotalAlerts(): number {
    return this.radialData.reduce((sum, zone) => sum + zone.data, 0);
  }

  getZoneWithMostAlerts(): { name: string; count: number } {
    const max = Math.max(...this.radialData.map((z) => z.data));
    return (
      this.radialData.find((z) => z.data === max) || { name: "", count: 0 }
    );
  }

  private colorPicker() {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  }

  protected readonly faDroplet = faDroplet;
  protected readonly faCalendar = faCalendar;
  protected readonly faBell = faBell;
  protected readonly faTemperature2 = faTemperature2;
}
